"use client"

import { useEffect, useRef, useState } from 'react'
import './arkanoid/styles/arkanoid.css'
import { createGame, STATE } from './arkanoid/core/game.js'
import { createStorage } from './arkanoid/core/storage.js'
import { createRenderer } from './arkanoid/render/renderer.js'
import { createSynth } from './arkanoid/audio/synth.js'
import { createSfx } from './arkanoid/audio/sfx.js'
import { createMusic } from './arkanoid/audio/music.js'
import { createInput } from './arkanoid/systems/input.js'
import { createHud } from './arkanoid/ui/hud.js'
import { createOverlays } from './arkanoid/ui/overlays.js'
import { createControls } from './arkanoid/ui/controls.js'

export default function ArkanoidApp() {
  const containerRef = useRef(null)
  const [sfxOn, setSfxOn] = useState(true)
  const [musicOn, setMusicOn] = useState(true)

  useEffect(() => {
    const root = containerRef.current
    if (!root) return

    const canvas = root.querySelector('#stage')
    const hudRoot = root.querySelector('#hud')
    const overlayRoot = root.querySelector('#overlays')
    const touchRoot = root.querySelector('#touchpad')
    if (!canvas || !hudRoot || !overlayRoot || !touchRoot) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const storage = createStorage()
    const renderer = createRenderer(canvas)
    const synth = createSynth()
    const sfx = createSfx(synth)
    const music = createMusic(synth)
    const hud = createHud(hudRoot)
    const overlays = createOverlays(overlayRoot)

    const initialSfx = storage.get('sfxOn') ?? true
    const initialMusic = storage.get('musicOn') ?? true
    synth.state.sfxOn = initialSfx
    synth.state.musicOn = initialMusic
    setSfxOn(initialSfx)
    setMusicOn(initialMusic)

    const game = createGame({
      renderer,
      storage,
      sfx,
      music,
      hud,
      overlays,
      reducedMotion: prefersReducedMotion,
    })

    let audioUnlocked = false
    function unlockAudio() {
      if (audioUnlocked) return
      audioUnlocked = true
      synth.unlock().then(() => {
        synth.setSfxEnabled(storage.get('sfxOn'))
        synth.setMusicEnabled(storage.get('musicOn'))
        if (game.state === STATE.TITLE) music.play('title')
      })
    }

    let helpReturn = null
    function openHelp() {
      helpReturn = game.state === STATE.PAUSED ? 'pause' : 'title'
      if (game.state === STATE.PLAYING) {
        game.togglePause()
        helpReturn = 'pause'
      }
      overlays.show('help')
    }

    function closeHelp() {
      if (helpReturn === 'pause') {
        overlays.show('pause')
      } else {
        overlays.show('title', { high: String(storage.get('highScore') || 0).padStart(6, '0') })
      }
      helpReturn = null
    }

    function handlePauseKey() {
      if (overlays.active === 'help') {
        closeHelp()
        return
      }
      if (overlays.active === 'title' || overlays.active === 'gameOver' || overlays.active === 'victory') {
        return
      }
      game.togglePause()
    }

    const input = createInput({
      canvas,
      toLogicalX: (clientX) => renderer.toLogicalX(clientX),
      onFire: () => {
        unlockAudio()
        if (overlays.active) return
        game.fire()
      },
      onPause: () => {
        unlockAudio()
        handlePauseKey()
      },
      onAnyInput: unlockAudio,
    })
    game.setInput(input)

    const controls = createControls({
      root: touchRoot,
      input,
      onFire: () => {
        unlockAudio()
        game.fire()
      },
      toggles: {
        '#btn-sfx': (on) => {
          storage.set('sfxOn', on)
          synth.setSfxEnabled(on)
          setSfxOn(on)
          if (on) sfx.uiSelect()
        },
        '#btn-music': (on) => {
          storage.set('musicOn', on)
          synth.setMusicEnabled(on)
          setMusicOn(on)
        },
      },
    })

    controls.setToggle('#btn-sfx', initialSfx)
    controls.setToggle('#btn-music', initialMusic)

    const actions = {
      pause: () => handlePauseKey(),
      start: () => game.newGame(),
      restart: () => game.newGame(),
      resume: () => game.togglePause(),
      title: () => game.quitToTitle(),
      help: openHelp,
      'close-help': closeHelp,
    }

    const handleActionClick = (event) => {
      const trigger = event.target.closest('[data-action]')
      if (!trigger || !root.contains(trigger)) return
      const action = actions[trigger.dataset.action]
      if (!action) return
      unlockAudio()
      sfx.uiSelect()
      action()
    }
    root.addEventListener('click', handleActionClick)

    const handleKeyDown = (event) => {
      if (event.key === 'Tab') {
        overlays.trapFocus(event)
      }
    }
    root.addEventListener('keydown', handleKeyDown)

    const updateDimensions = () => {
      if (!root) return
      const rect = root.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      // Account for masthead (~46px), HUD (~38px), touchpad (~56px), and container padding (~24px)
      const maxH = Math.max(260, rect.height - 164)
      const maxW = Math.max(200, rect.width - 24)
      let w = Math.floor(maxH * (448 / 640))
      let h = maxH
      if (w > maxW) {
        w = maxW
        h = Math.floor(w * (640 / 448))
      }
      root.style.setProperty('--console-w', `${w}px`)
      root.style.setProperty('--screen-h', `${h}px`)
      renderer.resize()
    }

    const handleResize = () => {
      updateDimensions()
    }
    window.addEventListener('resize', handleResize)

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions()
    })
    resizeObserver.observe(root)

    updateDimensions()
    game.start()

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      root.removeEventListener('click', handleActionClick)
      root.removeEventListener('keydown', handleKeyDown)
      game.stop()
      music.stop()
      input.destroy()
      controls.destroy()
      synth.destroy()
    }
  }, [])

  return (
    <div ref={containerRef} className="arkanoid-app" tabIndex={-1}>
      {/* Top OS Toolbar / Masthead */}
      <header className="masthead">
        <div className="brand">
          <p className="brand__kicker">Arcade</p>
          <h1 className="brand__title">Vaus</h1>
        </div>

        <div className="masthead__actions">
          <button
            type="button"
            id="btn-sfx"
            className="chip-btn"
            aria-pressed={sfxOn ? "true" : "false"}
            title="Toggle sound effects"
          >
            <span aria-hidden="true">♪</span> SFX
          </button>
          <button
            type="button"
            id="btn-music"
            className="chip-btn"
            aria-pressed={musicOn ? "true" : "false"}
            title="Toggle music"
          >
            <span aria-hidden="true">♬</span> Music
          </button>
          <button
            type="button"
            className="chip-btn"
            data-action="help"
            title="How to play"
          >
            <span aria-hidden="true">?</span> Help
          </button>
          <button
            type="button"
            className="chip-btn chip-btn--strong"
            data-action="pause"
            title="Pause or resume (P)"
          >
            Pause
          </button>
        </div>
      </header>

      {/* Main Arcade Cabinet */}
      <main className="cabinet">
        <div className="console">
          {/* HUD Bar */}
          <div className="hud" id="hud">
            <div className="hud__cell">
              <span className="hud__label" id="lbl-score">Score</span>
              <span className="hud__value" id="hud-score" aria-labelledby="lbl-score">000000</span>
            </div>
            <div className="hud__cell hud__cell--high">
              <span className="hud__label" id="lbl-high">High</span>
              <span className="hud__value hud__value--dim" id="hud-high" aria-labelledby="lbl-high">000000</span>
            </div>
            <div className="hud__cell hud__cell--round">
              <span className="hud__label" id="lbl-round">Round</span>
              <span className="hud__value" id="hud-round" aria-labelledby="lbl-round">1/12</span>
              <span className="hud__note" id="hud-round-name">Opening Salvo</span>
            </div>
            <div className="hud__cell hud__cell--lives">
              <span className="hud__label" id="lbl-lives">Vaus</span>
              <span className="hud__pips" id="hud-lives" aria-hidden="true"></span>
              <span className="visually-hidden" id="hud-lives-count">3 remaining</span>
            </div>
          </div>

          {/* Screen with Overlays and Canvas */}
          <div className="screen">
            <canvas
              id="stage"
              width="448"
              height="640"
              tabIndex={0}
              role="application"
              aria-label="Arkanoid playfield. Move Vaus with mouse drag, touch buttons, or arrow keys. Press space to launch the ball and fire the laser. Press P to pause."
            ></canvas>

            <div className="overlays" id="overlays" hidden>
              {/* Title Screen */}
              <section className="panel" data-screen="title" aria-labelledby="title-heading">
                <p className="panel__kicker">Vaus Edition</p>
                <h2 className="panel__title" id="title-heading">Break Every Brick</h2>
                <p className="panel__body">
                  Twelve rounds of classic arcade Arkanoid, seven capsules, a warp gate, and DOH waiting at the end.
                </p>
                <p className="panel__stat">High score <b data-field="high">000000</b></p>
                <div className="panel__actions">
                  <button type="button" className="btn btn--primary" data-action="start" data-autofocus>
                    Start Game
                  </button>
                  <button type="button" className="btn" data-action="help">How to Play</button>
                </div>
                <p className="panel__hint">Mouse, touch, or keyboard (← → / Space / P).</p>
              </section>

              {/* Pause Screen */}
              <section className="panel" data-screen="pause" aria-labelledby="pause-heading" hidden>
                <p className="panel__kicker">Paused</p>
                <h2 className="panel__title" id="pause-heading">Game Paused</h2>
                <div className="panel__actions">
                  <button type="button" className="btn btn--primary" data-action="resume" data-autofocus>
                    Resume
                  </button>
                  <button type="button" className="btn" data-action="help">Controls</button>
                  <button type="button" className="btn btn--quiet" data-action="title">Quit to Title</button>
                </div>
              </section>

              {/* Help / Controls Screen */}
              <section className="panel panel--wide" data-screen="help" aria-labelledby="help-heading" hidden>
                <p className="panel__kicker">How to Play</p>
                <h2 className="panel__title" id="help-heading">Controls &amp; Capsules</h2>

                <div className="help-grid">
                  <div>
                    <h3 className="help-grid__head">Controls</h3>
                    <dl className="keylist">
                      <dt>Mouse</dt><dd>Steer, click to fire</dd>
                      <dt>Touch</dt><dd>Drag paddle, tap FIRE</dd>
                      <dt>← → / A D</dt><dd>Steer Vaus</dd>
                      <dt>Space</dt><dd>Launch and fire</dd>
                      <dt>P / Esc</dt><dd>Pause</dd>
                    </dl>
                  </div>
                  <div>
                    <h3 className="help-grid__head">Capsules</h3>
                    <ul className="capsule-list">
                      <li><span className="cap cap--L">L</span> Laser cannons</li>
                      <li><span className="cap cap--E">E</span> Enlarge Vaus</li>
                      <li><span className="cap cap--C">C</span> Catch the ball</li>
                      <li><span className="cap cap--S">S</span> Slow the ball</li>
                      <li><span className="cap cap--D">D</span> Triple balls</li>
                      <li><span className="cap cap--B">B</span> Warp gate</li>
                      <li><span className="cap cap--P">P</span> Extra life</li>
                      <li><span className="cap cap--R">R</span> Shrinks Vaus</li>
                    </ul>
                  </div>
                </div>

                <p className="panel__body">
                  Silver bricks take several hits and get tougher each round. Gold bricks never break.
                  Where the ball strikes Vaus decides the deflection angle.
                </p>

                <div className="panel__actions panel__actions--sticky">
                  <button type="button" className="btn btn--primary" data-action="close-help" data-autofocus>
                    Got It
                  </button>
                </div>
              </section>

              {/* Game Over Screen */}
              <section className="panel" data-screen="gameOver" aria-labelledby="over-heading" hidden>
                <p className="panel__kicker">Game Over</p>
                <h2 className="panel__title" id="over-heading">Out of Vaus</h2>
                <dl className="scoreboard">
                  <div><dt>Score</dt><dd data-field="score">000000</dd></div>
                  <div><dt>Round</dt><dd data-field="round">1 / 12</dd></div>
                  <div><dt>Bricks</dt><dd data-field="bricks">0</dd></div>
                </dl>
                <div className="panel__actions">
                  <button type="button" className="btn btn--primary" data-action="restart" data-autofocus>
                    Play Again
                  </button>
                  <button type="button" className="btn btn--quiet" data-action="title">Title Screen</button>
                </div>
              </section>

              {/* Victory Screen */}
              <section className="panel" data-screen="victory" aria-labelledby="win-heading" hidden>
                <p className="panel__kicker">DOH Defeated</p>
                <h2 className="panel__title" id="win-heading">Dimension Cleared</h2>
                <dl className="scoreboard">
                  <div><dt>Score</dt><dd data-field="score">000000</dd></div>
                  <div><dt>Bricks</dt><dd data-field="bricks">0</dd></div>
                  <div><dt>Capsules</dt><dd data-field="capsules">0</dd></div>
                </dl>
                <div className="panel__actions">
                  <button type="button" className="btn btn--primary" data-action="restart" data-autofocus>
                    Play Again
                  </button>
                  <button type="button" className="btn btn--quiet" data-action="title">Title Screen</button>
                </div>
              </section>
            </div>
          </div>

          {/* Mobile / Touchpad D-Pad */}
          <div className="touchpad" id="touchpad">
            <button
              type="button"
              className="pad-btn"
              data-hold="left"
              aria-label="Move Vaus left"
            >
              <span aria-hidden="true">◀</span>
            </button>
            <button
              type="button"
              className="pad-btn pad-btn--fire"
              data-fire
              aria-label="Launch and fire"
            >
              FIRE
            </button>
            <button
              type="button"
              className="pad-btn"
              data-hold="right"
              aria-label="Move Vaus right"
            >
              <span aria-hidden="true">▶</span>
            </button>
          </div>
        </div>
      </main>

      <div className="visually-hidden" id="announcer" role="status" aria-live="polite"></div>
    </div>
  )
}
